import { LOGIN_PATH } from '@/config'
/**
 * The admin shell.
 *
 * Denser than the public side and unapologetically so — this is an operator's
 * console, and the person using it is looking at it all day.
 *
 * The one thing it insists on surfacing is the task runner. When background
 * work is running on the in-process fallback, queued crawls are lost on
 * restart, and an operator must never discover that from a missing job.
 */

import { useQuery } from '@tanstack/react-query'
import { NavLink, Navigate, Outlet, Link } from 'react-router-dom'
import {
  ChartIcon, InboxIcon, Logo, LogoutIcon, MoonIcon, SettingsIcon,
  BuildingIcon, SourceIcon, SpiderIcon, SunIcon, UsersIcon,
} from '@/components/ui/icons'
import { Badge, Button, cx, Spinner } from '@/components/ui/primitives'
import { adminApi } from '@/services/endpoints'
import { useAuth } from '@/stores/auth'
import { useTheme } from '@/stores/theme'

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: ChartIcon, end: true },
  { to: '/admin/organisations', label: 'Organisations', icon: BuildingIcon },
  { to: '/admin/crawls', label: 'Ingestion', icon: SpiderIcon },
  { to: '/admin/review', label: 'Review queue', icon: InboxIcon, badge: 'review' },
  { to: '/admin/seo', label: 'SEO health', icon: SourceIcon },
  { to: '/admin/users', label: 'Users', icon: UsersIcon, adminOnly: true },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon, adminOnly: true },
] as const

export function AdminLayout() {
  const { user, isStaff, loading, logout } = useAuth()
  const { resolved, cycle } = useTheme()

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: adminApi.dashboard,
    // The pending count in the sidebar should not go stale while an operator
    // works through the queue.
    refetchInterval: 60_000,
    enabled: isStaff,
  })

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-6 text-[var(--text-tertiary)]" />
      </div>
    )
  }

  // The server enforces this on every endpoint; the redirect is a courtesy so a
  // non-staff user sees a sign-in page rather than a wall of 403s.
  if (!isStaff) return <Navigate to={`${LOGIN_PATH}?next=/admin`} replace />

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const pending = dashboard.data?.review_queue.pending ?? 0

  return (
    <div className="flex min-h-screen bg-[var(--surface-page)]">
      {/* -- Sidebar -------------------------------------------------------- */}
      <aside className="hidden w-56 shrink-0 border-r border-[var(--border-subtle)] bg-[var(--surface-panel)] lg:flex lg:flex-col">
        <div className="flex h-14 items-center gap-2.5 border-b border-[var(--border-subtle)] px-4">
          <Logo size={24} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
              NaijaIndex
            </p>
            <p className="text-2xs text-[var(--text-tertiary)]">Operations</p>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          {NAV.filter((item) => !('adminOnly' in item && item.adminOnly) || isAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--accent-subtle)] font-medium text-[var(--accent-text)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
                )
              }
            >
              <item.icon size={16} />
              <span className="flex-1">{item.label}</span>
              {'badge' in item && item.badge === 'review' && pending > 0 ? (
                <span className="rounded-full bg-[var(--color-caution)] px-1.5 text-2xs font-semibold tabular text-white">
                  {pending}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        {/* The runner warning lives where it cannot be missed. */}
        {dashboard.data && !dashboard.data.crawling.runner_durable ? (
          <div className="mx-3 mb-3 rounded-md border border-[color-mix(in_oklch,var(--color-caution)_35%,transparent)] bg-[color-mix(in_oklch,var(--color-caution)_10%,transparent)] p-2.5">
            <p className="text-2xs font-semibold text-[var(--color-caution)]">
              In-process worker
            </p>
            <p className="mt-0.5 text-2xs leading-relaxed text-[var(--text-secondary)]">
              Queued work is resubmitted after a restart, but the pool does not
              scale past one machine. Set <code>REDIS_URL</code> for Celery.
            </p>
          </div>
        ) : null}

        <div className="border-t border-[var(--border-subtle)] p-3">
          <div className="flex items-center gap-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-subtle)] text-2xs font-semibold text-[var(--accent-text)]">
              {user?.first_name?.[0]}
              {user?.last_name?.[0]}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-[var(--text-primary)]">
                {user?.full_name}
              </p>
              <p className="truncate text-2xs text-[var(--text-tertiary)]">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
          <div className="mt-2 flex gap-1">
            <Button variant="ghost" size="sm" onClick={cycle} className="flex-1 px-2">
              {resolved === 'dark' ? <SunIcon size={14} /> : <MoonIcon size={14} />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => void logout()} className="flex-1 px-2">
              <LogoutIcon size={14} />
            </Button>
          </div>
          <Link
            to="/"
            className="mt-1.5 block rounded-md px-2 py-1.5 text-center text-2xs text-[var(--text-tertiary)] hover:bg-[var(--surface-hover)]"
          >
            View public site
          </Link>
        </div>
      </aside>

      {/* -- Content -------------------------------------------------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile bar. The sidebar is hidden below `lg` because an operator
            console is not a phone experience — but it must still be reachable. */}
        <div className="flex h-14 items-center gap-3 border-b border-[var(--border-subtle)] bg-[var(--surface-panel)] px-4 lg:hidden">
          <Logo size={22} />
          <span className="text-sm font-semibold">Operations</span>
          <div className="flex-1" />
          {pending > 0 ? (
            <Link to="/admin/review">
              <Badge tone="caution" size="xs">{pending} to review</Badge>
            </Link>
          ) : null}
        </div>

        <nav className="flex gap-1 overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-2 no-scrollbar lg:hidden">
          {NAV.filter((item) => !('adminOnly' in item && item.adminOnly) || isAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                cx(
                  'shrink-0 rounded-md px-3 py-1.5 text-xs transition-colors',
                  isActive
                    ? 'bg-[var(--accent-subtle)] font-medium text-[var(--accent-text)]'
                    : 'text-[var(--text-secondary)]',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
